import React, { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Breadcrumbs,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Link as MuiLink,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Paper,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  CreateNewFolder,
  Description,
  ExpandLess,
  ExpandMore,
  Folder,
  FolderOpen,
  Home,
  Link as LinkIcon,
  NavigateNext,
  Refresh,
  UploadFile,
  Visibility,
} from '@mui/icons-material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useAuth } from '../../contexts/AuthContext';
import { userHasPermission } from '../../utils/permissions';
import { messageFromApiBody, toUserErrorMessage } from '../../utils/apiError';
import CaseAutocomplete, { CaseAutocompleteOption } from '../../components/Common/CaseAutocomplete';
import DocumentPreviewDialog from '../../components/Documents/DocumentPreviewDialog';
import ListLoadErrorAlert from '../../components/Common/ListLoadErrorAlert';
import { getAuthToken } from '../../lib/authToken';

interface DriveFolder {
  id: string;
  name: string;
  path: string;
  caseId?: string | null;
  parentId?: string | null;
  children?: DriveFolder[];
}

interface DriveDocument {
  id: string;
  title: string;
  originalName?: string;
  mimeType?: string;
  fileSize?: number;
  documentType?: string;
  folderId?: string | null;
  caseId?: string | null;
  updatedAt?: string;
}

const authHeaders = () => ({
  Authorization: `Bearer ${getAuthToken()}`,
});

const formatBytes = (n?: number) => {
  if (!n && n !== 0) return '—';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
};

type DragPayload =
  | { type: 'document'; id: string }
  | { type: 'folder'; id: string };

const DRAG_MIME = 'application/x-argislegal-drive';

function FolderTreeNode({
  node,
  depth,
  activeFolderId,
  canWrite,
  onNavigate,
  onDropTarget,
  onShareFolder,
}: {
  node: DriveFolder;
  depth: number;
  activeFolderId: string;
  canWrite: boolean;
  onNavigate: (folderId: string) => void;
  onDropTarget: (targetFolderId: string | null, payload: DragPayload) => void;
  onShareFolder: (folderId: string) => void;
}) {
  const [open, setOpen] = useState(true);
  const [dragOver, setDragOver] = useState(false);
  const hasKids = Boolean(node.children?.length);
  const selected = activeFolderId === node.id;

  return (
    <Box>
      <ListItemButton
        selected={selected}
        sx={{
          pl: 1 + depth * 1.5,
          borderRadius: 1,
          bgcolor: dragOver ? 'action.selected' : undefined,
        }}
        onClick={() => onNavigate(node.id)}
        draggable={canWrite}
        onDragStart={(e) => {
          if (!canWrite) return;
          e.dataTransfer.setData(
            DRAG_MIME,
            JSON.stringify({ type: 'folder', id: node.id } satisfies DragPayload),
          );
          e.dataTransfer.effectAllowed = 'move';
        }}
        onDragOver={(e) => {
          if (!canWrite) return;
          e.preventDefault();
          e.dataTransfer.dropEffect = 'move';
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          if (!canWrite) return;
          e.preventDefault();
          e.stopPropagation();
          setDragOver(false);
          try {
            const raw = e.dataTransfer.getData(DRAG_MIME);
            if (!raw) return;
            const payload = JSON.parse(raw) as DragPayload;
            onDropTarget(node.id, payload);
          } catch {
            /* ignore */
          }
        }}
      >
        <ListItemIcon sx={{ minWidth: 28 }}>
          {hasKids ? (
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                setOpen((v) => !v);
              }}
            >
              {open ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
            </IconButton>
          ) : (
            <Box sx={{ width: 28 }} />
          )}
        </ListItemIcon>
        <ListItemIcon sx={{ minWidth: 32 }}>
          {selected ? <FolderOpen color="primary" fontSize="small" /> : <Folder color="primary" fontSize="small" />}
        </ListItemIcon>
        <ListItemText
          primary={node.name}
          primaryTypographyProps={{ variant: 'body2', noWrap: true }}
        />
        {canWrite && (
          <Tooltip title="Share folder">
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onShareFolder(node.id);
              }}
            >
              <LinkIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </ListItemButton>
      {hasKids && (
        <Collapse in={open} timeout="auto" unmountOnExit>
          {node.children!.map((child) => (
            <FolderTreeNode
              key={child.id}
              node={child}
              depth={depth + 1}
              activeFolderId={activeFolderId}
              canWrite={canWrite}
              onNavigate={onNavigate}
              onDropTarget={onDropTarget}
              onShareFolder={onShareFolder}
            />
          ))}
        </Collapse>
      )}
    </Box>
  );
}

const MatterDrivePage: React.FC = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const canWrite = userHasPermission(user, 'documents:write');

  const caseId = searchParams.get('caseId') || '';
  const folderId = searchParams.get('folderId') || '';
  const [caseOption, setCaseOption] = useState<CaseAutocompleteOption | string | null>(caseId || null);
  const [search, setSearch] = useState('');
  const [newFolderOpen, setNewFolderOpen] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [shareOpen, setShareOpen] = useState(false);
  const [shareUrl, setShareUrl] = useState('');
  const [previewDoc, setPreviewDoc] = useState<DriveDocument | null>(null);
  const [uploading, setUploading] = useState(false);
  const [rootDropOver, setRootDropOver] = useState(false);

  const invalidateDrive = () => {
    queryClient.invalidateQueries({ queryKey: ['matter-drive', user?.tenantId] });
    queryClient.invalidateQueries({ queryKey: ['matter-drive-tree', user?.tenantId] });
  };

  const driveQuery = useQuery({
    queryKey: ['matter-drive', user?.tenantId, caseId || null, folderId || null, search],
    enabled: Boolean(user?.tenantId),
    queryFn: async () => {
      const params = new URLSearchParams();
      if (caseId) params.set('caseId', caseId);
      if (folderId) params.set('folderId', folderId);
      if (search.trim()) params.set('q', search.trim());
      const res = await fetch(`/api/drive?${params}`, { headers: authHeaders() });
      const json = await res.json();
      if (!res.ok) throw new Error(messageFromApiBody(json, 'Failed to load drive'));
      return json.data as {
        folder: DriveFolder | null;
        case: { id: string; caseNumber?: string; title?: string } | null;
        breadcrumbs: DriveFolder[];
        folders: DriveFolder[];
        documents: DriveDocument[];
      };
    },
  });

  const treeQuery = useQuery({
    queryKey: ['matter-drive-tree', user?.tenantId, caseId || null],
    enabled: Boolean(user?.tenantId),
    queryFn: async () => {
      const params = new URLSearchParams();
      if (caseId) params.set('caseId', caseId);
      const res = await fetch(`/api/drive/tree?${params}`, { headers: authHeaders() });
      const json = await res.json();
      if (!res.ok) throw new Error(messageFromApiBody(json, 'Failed to load folder tree'));
      return (json.data?.tree || []) as DriveFolder[];
    },
  });

  const createFolderMutation = useMutation({
    mutationFn: async (name: string) => {
      const res = await fetch('/api/drive/folders', {
        method: 'POST',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name,
          parentId: folderId || null,
          caseId: caseId || null,
        }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(messageFromApiBody(json, 'Create folder failed'));
      return json.data;
    },
    onSuccess: () => {
      toast.success('Folder created');
      setNewFolderOpen(false);
      setNewFolderName('');
      invalidateDrive();
    },
    onError: (err: unknown) => toast.error(toUserErrorMessage(err, 'Create folder failed')),
  });

  const shareDocMutation = useMutation({
    mutationFn: async (documentId: string) => {
      const res = await fetch(`/api/drive/documents/${documentId}/share`, {
        method: 'POST',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ expiresInDays: 7 }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(messageFromApiBody(json, 'Share failed'));
      return json.data as { url: string };
    },
    onSuccess: async (data) => {
      setShareUrl(data.url);
      setShareOpen(true);
      try {
        await navigator.clipboard.writeText(data.url);
        toast.success('Share link copied');
      } catch {
        toast.success('Share link created');
      }
    },
    onError: (err: unknown) => toast.error(toUserErrorMessage(err, 'Share failed')),
  });

  const shareFolderMutation = useMutation({
    mutationFn: async (id: string) => {
      const res = await fetch(`/api/drive/folders/${id}/share`, {
        method: 'POST',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ expiresInDays: 7 }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(messageFromApiBody(json, 'Folder share failed'));
      return json.data as { url: string };
    },
    onSuccess: async (data) => {
      setShareUrl(data.url);
      setShareOpen(true);
      try {
        await navigator.clipboard.writeText(data.url);
        toast.success('Folder share link copied');
      } catch {
        toast.success('Folder share link created');
      }
    },
    onError: (err: unknown) => toast.error(toUserErrorMessage(err, 'Folder share failed')),
  });

  const moveMutation = useMutation({
    mutationFn: async ({
      payload,
      targetFolderId,
    }: {
      payload: DragPayload;
      targetFolderId: string | null;
    }) => {
      const body =
        payload.type === 'document'
          ? { documentIds: [payload.id], targetFolderId }
          : { folderIds: [payload.id], targetFolderId };
      const res = await fetch('/api/drive/items/move', {
        method: 'POST',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(messageFromApiBody(json, 'Move failed'));
      return json.data;
    },
    onSuccess: () => {
      toast.success('Moved');
      invalidateDrive();
    },
    onError: (err: unknown) => toast.error(toUserErrorMessage(err, 'Move failed')),
  });

  const navigateTo = (next: { caseId?: string | null; folderId?: string | null }) => {
    const params = new URLSearchParams();
    const c = next.caseId === undefined ? caseId : next.caseId;
    const f = next.folderId === undefined ? folderId : next.folderId;
    if (c) params.set('caseId', c);
    if (f) params.set('folderId', f);
    setSearchParams(params);
  };

  const onCaseChange = (next: CaseAutocompleteOption | null) => {
    setCaseOption(next);
    navigateTo({ caseId: next?.id || null, folderId: null });
  };

  const handleDropOnTarget = (targetFolderId: string | null, payload: DragPayload) => {
    if (payload.type === 'folder' && payload.id === targetFolderId) return;
    if (payload.type === 'document' && targetFolderId === folderId) return;
    moveMutation.mutate({ payload, targetFolderId });
  };

  const handleUpload = async (fileList: FileList | null) => {
    if (!fileList?.length || !canWrite) return;
    setUploading(true);
    try {
      for (const file of Array.from(fileList)) {
        const form = new FormData();
        form.append('file', file);
        form.append('title', file.name);
        if (caseId) form.append('caseId', caseId);
        if (folderId) form.append('folderId', folderId);
        const res = await fetch('/api/drive/upload', {
          method: 'POST',
          headers: authHeaders(),
          body: form,
        });
        const json = await res.json();
        if (!res.ok) throw new Error(messageFromApiBody(json, `Upload failed: ${file.name}`));
      }
      toast.success('Upload complete');
      invalidateDrive();
    } catch (err) {
      toast.error(toUserErrorMessage(err, 'Upload failed'));
    } finally {
      setUploading(false);
    }
  };

  const data = driveQuery.data;
  const title = useMemo(() => {
    if (data?.case) return `Matter Drive — ${data.case.caseNumber || ''} ${data.case.title || ''}`.trim();
    if (caseId) return 'Matter Drive';
    return 'Matter Drive (firm root)';
  }, [data?.case, caseId]);

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap', mb: 2 }}>
        <Box>
          <Typography variant="h5" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <FolderOpen color="primary" /> {title}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Drag files or folders onto the tree to move. Share folders or files with a link.
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <Button
            size="small"
            variant="outlined"
            startIcon={<Refresh />}
            onClick={() => {
              driveQuery.refetch();
              treeQuery.refetch();
            }}
          >
            Refresh
          </Button>
          {canWrite && (
            <>
              <Button
                size="small"
                variant="outlined"
                startIcon={<CreateNewFolder />}
                onClick={() => setNewFolderOpen(true)}
              >
                New folder
              </Button>
              <Button
                size="small"
                variant="contained"
                component="label"
                startIcon={uploading ? <CircularProgress size={16} color="inherit" /> : <UploadFile />}
                disabled={uploading}
              >
                Upload
                <input
                  hidden
                  type="file"
                  multiple
                  onChange={(e) => handleUpload(e.target.files)}
                />
              </Button>
            </>
          )}
        </Box>
      </Box>

      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={6}>
            <CaseAutocomplete
              value={caseOption}
              onChange={onCaseChange}
              label="Filter by case (optional)"
              size="small"
              fullWidth
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              size="small"
              fullWidth
              placeholder="Search files in this matter / firm…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </Grid>
        </Grid>
      </Paper>

      <Grid container spacing={2}>
        <Grid item xs={12} md={4} lg={3}>
          <Paper variant="outlined" sx={{ p: 1, minHeight: 420 }}>
            <Typography variant="subtitle2" color="text.secondary" sx={{ px: 1, mb: 1 }}>
              Folders
            </Typography>
            <List dense disablePadding>
              <ListItemButton
                selected={!folderId}
                sx={{ borderRadius: 1, bgcolor: rootDropOver ? 'action.selected' : undefined }}
                onClick={() => navigateTo({ folderId: null })}
                onDragOver={(e) => {
                  if (!canWrite) return;
                  e.preventDefault();
                  setRootDropOver(true);
                }}
                onDragLeave={() => setRootDropOver(false)}
                onDrop={(e) => {
                  if (!canWrite) return;
                  e.preventDefault();
                  setRootDropOver(false);
                  try {
                    const raw = e.dataTransfer.getData(DRAG_MIME);
                    if (!raw) return;
                    handleDropOnTarget(null, JSON.parse(raw) as DragPayload);
                  } catch {
                    /* ignore */
                  }
                }}
              >
                <ListItemIcon sx={{ minWidth: 32 }}>
                  <Home fontSize="small" />
                </ListItemIcon>
                <ListItemText primary="Root" primaryTypographyProps={{ variant: 'body2' }} />
              </ListItemButton>
              {(treeQuery.data || []).map((node) => (
                <FolderTreeNode
                  key={node.id}
                  node={node}
                  depth={0}
                  activeFolderId={folderId}
                  canWrite={canWrite}
                  onNavigate={(id) => navigateTo({ folderId: id })}
                  onDropTarget={handleDropOnTarget}
                  onShareFolder={(id) => shareFolderMutation.mutate(id)}
                />
              ))}
              {!treeQuery.isLoading && (treeQuery.data || []).length === 0 && (
                <Typography variant="body2" color="text.secondary" sx={{ px: 2, py: 1 }}>
                  {caseId
                    ? 'No folders yet — open a matter or create one.'
                    : 'Select a case for matter folders, or create a firm folder.'}
                </Typography>
              )}
            </List>
          </Paper>
        </Grid>

        <Grid item xs={12} md={8} lg={9}>
          <Paper variant="outlined" sx={{ p: 2, minHeight: 420 }}>
            <Breadcrumbs separator={<NavigateNext fontSize="small" />} sx={{ mb: 2 }}>
              <MuiLink
                component="button"
                underline="hover"
                color="inherit"
                onClick={() => navigateTo({ folderId: null })}
                sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
              >
                <Home fontSize="small" /> Root
              </MuiLink>
              {(data?.breadcrumbs || []).map((crumb) => (
                <MuiLink
                  key={crumb.id}
                  component="button"
                  underline="hover"
                  color="inherit"
                  onClick={() => navigateTo({ folderId: crumb.id })}
                >
                  {crumb.name}
                </MuiLink>
              ))}
            </Breadcrumbs>

            {driveQuery.isError && (
              <ListLoadErrorAlert
                resourceLabel="drive"
                message={toUserErrorMessage(driveQuery.error, 'Failed to load Matter Drive')}
                onRetry={() => driveQuery.refetch()}
              />
            )}

            {driveQuery.isLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                <CircularProgress />
              </Box>
            ) : (
              <Grid container spacing={2}>
                <Grid item xs={12} md={5}>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                    Subfolders
                  </Typography>
                  <List dense>
                    {(data?.folders || []).length === 0 ? (
                      <Typography variant="body2" color="text.secondary" sx={{ px: 2 }}>
                        No subfolders here.
                      </Typography>
                    ) : (
                      data?.folders.map((folder) => (
                        <ListItemButton
                          key={folder.id}
                          onClick={() => navigateTo({ folderId: folder.id })}
                          draggable={canWrite}
                          onDragStart={(e) => {
                            if (!canWrite) return;
                            e.dataTransfer.setData(
                              DRAG_MIME,
                              JSON.stringify({ type: 'folder', id: folder.id } satisfies DragPayload),
                            );
                            e.dataTransfer.effectAllowed = 'move';
                          }}
                        >
                          <ListItemIcon sx={{ minWidth: 36 }}>
                            <Folder color="primary" />
                          </ListItemIcon>
                          <ListItemText primary={folder.name} secondary={folder.path} />
                          {canWrite && (
                            <Tooltip title="Share folder">
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  shareFolderMutation.mutate(folder.id);
                                }}
                              >
                                <LinkIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </ListItemButton>
                      ))
                    )}
                  </List>
                </Grid>
                <Grid item xs={12} md={7}>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                    Files {canWrite ? '(drag onto a folder to move)' : ''}
                  </Typography>
                  <List dense>
                    {(data?.documents || []).length === 0 ? (
                      <Typography variant="body2" color="text.secondary" sx={{ px: 2 }}>
                        No files in this folder.
                      </Typography>
                    ) : (
                      data?.documents.map((doc) => (
                        <ListItemButton
                          key={doc.id}
                          onClick={() => setPreviewDoc(doc)}
                          draggable={canWrite}
                          onDragStart={(e) => {
                            if (!canWrite) return;
                            e.dataTransfer.setData(
                              DRAG_MIME,
                              JSON.stringify({ type: 'document', id: doc.id } satisfies DragPayload),
                            );
                            e.dataTransfer.effectAllowed = 'move';
                          }}
                        >
                          <ListItemIcon sx={{ minWidth: 36 }}>
                            <Description />
                          </ListItemIcon>
                          <ListItemText
                            primary={doc.title}
                            secondary={`${doc.originalName || ''} · ${formatBytes(doc.fileSize)}`}
                          />
                          <Chip size="small" label={doc.documentType || 'file'} sx={{ mr: 1 }} />
                          <Tooltip title="Preview">
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                setPreviewDoc(doc);
                              }}
                            >
                              <Visibility fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          {canWrite && (
                            <Tooltip title="Copy share link">
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  shareDocMutation.mutate(doc.id);
                                }}
                              >
                                <LinkIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </ListItemButton>
                      ))
                    )}
                  </List>
                </Grid>
              </Grid>
            )}
          </Paper>
        </Grid>
      </Grid>

      <Dialog open={newFolderOpen} onClose={() => setNewFolderOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>New folder</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            size="small"
            label="Folder name"
            value={newFolderName}
            onChange={(e) => setNewFolderName(e.target.value)}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNewFolderOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={!newFolderName.trim() || createFolderMutation.isPending}
            onClick={() => createFolderMutation.mutate(newFolderName.trim())}
          >
            Create
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={shareOpen} onClose={() => setShareOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Share link</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            Anyone with this link can view until it expires (7 days). Folder links list contents and allow file download.
          </Alert>
          <TextField fullWidth size="small" value={shareUrl} InputProps={{ readOnly: true }} />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={async () => {
              await navigator.clipboard.writeText(shareUrl);
              toast.success('Copied');
            }}
          >
            Copy
          </Button>
          <Button variant="contained" onClick={() => setShareOpen(false)}>
            Done
          </Button>
        </DialogActions>
      </Dialog>

      {previewDoc && (
        <DocumentPreviewDialog
          open={Boolean(previewDoc)}
          onClose={() => setPreviewDoc(null)}
          document={previewDoc as any}
        />
      )}
    </Box>
  );
};

export default MatterDrivePage;
